import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/PersonasService';
import { Persona } from '../types';
import { add as addAlias, deleteRecord as deleteAlias } from '../../aliases/services/AliasesService';
import { Alias, AliasStatus } from '../../aliases/types';

export const usePersona = (personaId?: string) => {
    const queryClient = useQueryClient();

    const personaQuery = useQuery({
        queryKey: ['persona', personaId],
        queryFn: () => {
            if (!personaId) throw new Error('personaId is required');
            return fetch(personaId);
        },
        enabled: !!personaId
    });

    const createMutation = useMutation({
        mutationFn: async (values: Persona) => {
            const { created, updated, ...rest } = values;
            await addAlias({
                name: [values.givenName, values.familyName].join('.').toLowerCase(),
                aliasStatus: AliasStatus.ENABLED,
                createdFromPersona: true,
            } as Alias);
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personas'] });
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (record: Persona) => {
            if (!personaId) throw new Error('personaId is required');
            return update(personaId, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personas'] });
            queryClient.invalidateQueries({ queryKey: ['persona', personaId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!personaId) throw new Error('personaId is required');
            const record = personaQuery.data;
            if (!record || !record.email) throw new Error('record with email is required');
            const aliasName = [record.givenName, record.familyName].join('.');
            await deleteAlias(personaId, aliasName);
            return deleteRecord(personaId, record.email);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personas'] });
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const fetchRecord = () => {
        personaQuery.refetch();
    };

    const createRecord = (values: Persona) => {
        createMutation.mutate(values);
    };

    const updateRecord = (values: Persona) => {
        updateMutation.mutate(values);
    };

    const deleteRecordAction = () => {
        deleteMutation.mutate();
    };

    return {
        record: personaQuery.data,
        loading: personaQuery.isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord: deleteRecordAction
    };
};
